import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, KeyRound, UserX, UserCheck, Copy } from "lucide-react";

const DEMO_DOMAIN = "@loanapp.local";

export default function UsersAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", password: "", mobile: "", role: "reviewer" });

  // A role change can grant Admin, and a deactivation locks someone out, so both confirm.
  const [pendingRole, setPendingRole] = useState(null);
  const [pendingActive, setPendingActive] = useState(null);
  const [pendingReset, setPendingReset] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/users").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false);
      setForm({ full_name: "", email: "", password: "", mobile: "", role: "reviewer" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const confirmRole = async () => {
    if (!pendingRole) return;
    setBusy(true);
    try {
      await api.patch("/users/" + pendingRole.user.id, { role: pendingRole.role });
      toast.success(pendingRole.user.email + " is now " + pendingRole.role);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); setPendingRole(null); }
  };

  const confirmActive = async () => {
    if (!pendingActive) return;
    setBusy(true);
    try {
      await api.patch("/users/" + pendingActive.user.id, { active: pendingActive.next });
      toast.success(pendingActive.next ? "Account reactivated" : "Account deactivated - they can no longer sign in");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); setPendingActive(null); }
  };

  const confirmReset = async () => {
    if (!pendingReset) return;
    setBusy(true);
    try {
      const r = await api.post("/users/" + pendingReset.user.id + "/reset-password", {});
      setResetResult(r.data);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); setPendingReset(null); }
  };

  const copyTemp = async () => {
    try {
      await navigator.clipboard.writeText(resetResult.temporary_password);
      toast.success("Copied");
    } catch (e) { toast.error("Could not copy - select the text instead"); }
  };

  const isActive = (u) => u.active !== false;
  const statusClass = (u) => isActive(u)
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : "bg-slate-100 text-slate-500 border-slate-200";

  return (
    <Layout>
      <div className="fade-in space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="text-2xl font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>Users</h1>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-blue-900 hover:bg-blue-800" data-testid="new-user-btn"><Plus className="h-4 w-4 mr-1"/> New user</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create user</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Full name</Label><Input data-testid="new-user-name" value={form.full_name} onChange={(e)=>setForm({...form, full_name: e.target.value})}/></div>
                <div><Label>Email</Label><Input data-testid="new-user-email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})}/></div>
                <div><Label>Mobile</Label><Input data-testid="new-user-mobile" value={form.mobile} onChange={(e)=>setForm({...form, mobile: e.target.value})}/></div>
                <div><Label>Password</Label><Input type="password" data-testid="new-user-pwd" value={form.password} onChange={(e)=>setForm({...form, password: e.target.value})}/></div>
                <div><Label>Role</Label>
                  <Select value={form.role} onValueChange={(v)=>setForm({...form, role: v})}>
                    <SelectTrigger data-testid="new-user-role"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="borrower">Borrower</SelectItem>
                      <SelectItem value="reviewer">Reviewer</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter><Button onClick={save} className="bg-blue-900 hover:bg-blue-800" data-testid="save-user-btn">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <Card className="rounded-xl border-slate-200 hidden lg:block"><CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2">Name</th>
                  <th className="text-left px-4 py-2">Email</th>
                  <th className="text-left px-4 py-2">Role</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Created</th>
                  <th className="text-right px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((u) => (
                  <tr key={u.id} className="border-t border-slate-100" data-testid={"user-row-" + u.id}>
                    <td className="px-4 py-2">
                      {u.full_name}
                      {u.email && u.email.endsWith(DEMO_DOMAIN) && (
                        <span className="ml-2 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">Demo</span>
                      )}
                    </td>
                    <td className="px-4 py-2">{u.email}</td>
                    <td className="px-4 py-2">
                      <Select value={u.role} onValueChange={(v)=> { if (v !== u.role) setPendingRole({ user: u, role: v }); }}>
                        <SelectTrigger className="w-32 h-8" data-testid={"role-select-" + u.id}><SelectValue/></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="borrower">Borrower</SelectItem>
                          <SelectItem value="reviewer">Reviewer</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-2">
                      <span className={"text-xs font-semibold px-2 py-0.5 rounded-full border " + statusClass(u)} data-testid={"user-status-" + u.id}>
                        {isActive(u) ? "Active" : "Deactivated"}
                      </span>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-slate-500">{new Date(u.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-2">
                      <div className="flex justify-end gap-2 whitespace-nowrap">
                        <Button variant="outline" size="sm" className="h-8 px-2 text-xs"
                          onClick={()=>setPendingReset({ user: u })} data-testid={"reset-pwd-" + u.id}>
                          <KeyRound className="h-3.5 w-3.5 mr-1"/> Reset
                        </Button>
                        <Button variant="outline" size="sm"
                          className={"h-8 px-2 text-xs " + (isActive(u) ? "text-red-700 border-red-200 hover:bg-red-50" : "text-emerald-700 border-emerald-200 hover:bg-emerald-50")}
                          onClick={()=>setPendingActive({ user: u, next: !isActive(u) })} data-testid={"toggle-active-" + u.id}>
                          {isActive(u)
                            ? <span className="flex items-center"><UserX className="h-3.5 w-3.5 mr-1"/> Deactivate</span>
                            : <span className="flex items-center"><UserCheck className="h-3.5 w-3.5 mr-1"/> Reactivate</span>}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent></Card>

        <div className="space-y-3 lg:hidden">
          {items.map((u) => (
            <Card key={u.id} className="rounded-xl border-slate-200"><CardContent className="p-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold truncate">
                    {u.full_name}
                    {u.email && u.email.endsWith(DEMO_DOMAIN) && (
                      <span className="ml-2 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 align-middle">Demo</span>
                    )}
                  </div>
                  <div className="text-sm text-slate-500 truncate">{u.email}</div>
                </div>
                <span className={"shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full border " + statusClass(u)}>
                  {isActive(u) ? "Active" : "Deactivated"}
                </span>
              </div>
              <Select value={u.role} onValueChange={(v)=> { if (v !== u.role) setPendingRole({ user: u, role: v }); }}>
                <SelectTrigger className="h-10"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="borrower">Borrower</SelectItem>
                  <SelectItem value="reviewer">Reviewer</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" className="h-10" onClick={()=>setPendingReset({ user: u })}>
                  <KeyRound className="h-4 w-4 mr-1"/> Reset
                </Button>
                <Button variant="outline"
                  className={"h-10 " + (isActive(u) ? "text-red-700 border-red-200" : "text-emerald-700 border-emerald-200")}
                  onClick={()=>setPendingActive({ user: u, next: !isActive(u) })}>
                  {isActive(u) ? "Deactivate" : "Reactivate"}
                </Button>
              </div>
            </CardContent></Card>
          ))}
        </div>
      </div>

      <AlertDialog open={!!pendingRole} onOpenChange={(o)=>{ if (!o) setPendingRole(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change this user's role?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingRole && (
                <span>
                  <b>{pendingRole.user.email}</b> will change from <b>{pendingRole.user.role}</b> to <b>{pendingRole.role}</b>.
                  {pendingRole.role === "admin" && " An admin can approve loans, reset passwords and see every borrower's records."}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRole} disabled={busy} data-testid="confirm-role-btn">Change role</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pendingActive} onOpenChange={(o)=>{ if (!o) setPendingActive(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{pendingActive && pendingActive.next ? "Reactivate this account?" : "Deactivate this account?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingActive && (pendingActive.next
                ? <span><b>{pendingActive.user.email}</b> will be able to sign in again.</span>
                : <span><b>{pendingActive.user.email}</b> will be signed out and refused at login until an administrator reactivates them. Their records are kept.</span>)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmActive} disabled={busy} data-testid="confirm-active-btn">
              {pendingActive && pendingActive.next ? "Reactivate" : "Deactivate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pendingReset} onOpenChange={(o)=>{ if (!o) setPendingReset(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset this user's password?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingReset && (
                <span>A new temporary password will be generated for <b>{pendingReset.user.email}</b>. Their current password stops working immediately, and you will be shown the new one once.</span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmReset} disabled={busy} data-testid="confirm-reset-btn">Reset password</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!resetResult} onOpenChange={(o)=>{ if (!o) setResetResult(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Temporary password</DialogTitle>
            <DialogDescription>
              For {resetResult && resetResult.email}. This is shown once - it is not stored anywhere you can read it again.
              Give it to them over a trusted channel and ask them to change it.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <code className="flex-1 px-3 py-3 rounded-lg bg-slate-100 font-mono text-base tracking-wider break-all" data-testid="temp-password">
              {resetResult && resetResult.temporary_password}
            </code>
            <Button variant="outline" onClick={copyTemp} className="h-12"><Copy className="h-4 w-4"/></Button>
          </div>
          <DialogFooter><Button onClick={()=>setResetResult(null)} className="bg-blue-900 hover:bg-blue-800">Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
